import React, { useEffect, useMemo, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Package } from "lucide-react";
import { toast } from "sonner";

export default function CalculatorPage() {
  const [containers, setContainers] = useState([]);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [body, setBody] = useState({ L: 5791, W: 2280, H: 2280 });
  const [crate, setCrate] = useState({ l: 1200, w: 800, h: 900 });

  useEffect(() => { api.get("/containers").then(r => setContainers(r.data)); }, []);

  const matches = useMemo(() => {
    if (!q.trim()) return containers.slice(0, 20);
    const t = q.toLowerCase();
    return containers.filter(c => `${c.name} ${c.category} ${c.model} ${c.class_code}`.toLowerCase().includes(t)).slice(0, 20);
  }, [containers, q]);

  const pick = (c) => {
    setSelectedId(c.id);
    if (c.length_mm && c.width_mm && c.height_mm) {
      setBody({ L: c.length_mm, W: c.width_mm, H: c.height_mm });
    } else {
      toast.info("No dimensions on file for this container — enter sizes below.");
    }
  };

  const fit = (cl, cw) => {
    const along = body.L > 0 && cl > 0 ? Math.floor(body.L / cl) : 0;
    const across = body.W > 0 && cw > 0 ? Math.floor(body.W / cw) : 0;
    const layers = body.H > 0 && crate.h > 0 ? Math.floor(body.H / crate.h) : 0;
    return { along, across, layers, total: along * across * layers };
  };

  const normal = fit(crate.l, crate.w);
  const rotated = fit(crate.w, crate.l);
  const best = rotated.total > normal.total ? { ...rotated, rotated: true } : { ...normal, rotated: false };
  const bodyM3 = (body.L * body.W * body.H) / 1e9;
  const crateM3 = (crate.l * crate.w * crate.h) / 1e9;
  const usedM3 = best.total * crateM3;
  const usedPct = bodyM3 > 0 ? (usedM3 / bodyM3) * 100 : 0;
  const orientL = best.rotated ? crate.w : crate.l;
  const orientW = best.rotated ? crate.l : crate.w;

  const DimInput = ({ label, value, onChange, tid }) => (
    <div>
      <Label className="text-eyebrow">{label}</Label>
      <Input type="number" value={value} onChange={e => onChange(Number(e.target.value))} data-testid={tid}
        className="mt-1.5 h-11 rounded-xl bg-white font-mono-tab" />
    </div>
  );

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-900">Crate & Container Calculator</h1>
      <p className="text-sm text-slate-500 mt-1">Enter the covering body and crate sizes to see the best load pattern.</p>

      <div className="fc-card p-4 mt-4">
        <div className="text-eyebrow mb-2">Load a standard container (search by name)</div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by model, category or class code…"
            data-testid="calc-container-search-input" className="pl-10 h-11 rounded-xl bg-white" />
        </div>
        <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-xl">
          {matches.map(c => (
            <button key={c.id} onClick={() => pick(c)} data-testid={`calc-container-option-${c.id}`}
              className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 transition-colors ${selectedId === c.id ? "bg-blue-50" : "hover:bg-slate-50"}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{c.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{c.category}{c.class_code ? ` · ${c.class_code}` : ""}</div>
              </div>
              {c.final_price != null && <div className="text-sm font-semibold text-blue-600 tabular-nums shrink-0">{fmtINR(c.final_price)}</div>}
            </button>
          ))}
          {matches.length === 0 && <div className="px-4 py-6 text-sm text-slate-500 text-center">No containers found.</div>}
        </div>
        <p className="text-xs text-slate-400 mt-2">You can also type your own sizes below.</p>
      </div>

      <div className="fc-card p-4 mt-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-3">
          <Package className="w-4 h-4 text-blue-600" /> Covering body / container (mm)
        </div>
        <div className="grid grid-cols-3 gap-3">
          <DimInput label="Length" value={body.L} onChange={v => { setBody({ ...body, L: v }); setSelectedId(null); }} tid="crate-calc-length-input" />
          <DimInput label="Width" value={body.W} onChange={v => { setBody({ ...body, W: v }); setSelectedId(null); }} tid="crate-calc-width-input" />
          <DimInput label="Height" value={body.H} onChange={v => { setBody({ ...body, H: v }); setSelectedId(null); }} tid="crate-calc-height-input" />
        </div>
      </div>

      <div className="fc-card p-4 mt-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-3">
          <Package className="w-4 h-4 text-blue-600" /> Crate (mm)
        </div>
        <div className="grid grid-cols-3 gap-3">
          <DimInput label="Length" value={crate.l} onChange={v => setCrate({ ...crate, l: v })} tid="crate-calc-crate-length-input" />
          <DimInput label="Width" value={crate.w} onChange={v => setCrate({ ...crate, w: v })} tid="crate-calc-crate-width-input" />
          <DimInput label="Height" value={crate.h} onChange={v => setCrate({ ...crate, h: v })} tid="crate-calc-crate-height-input" />
        </div>
      </div>

      <div className="fc-card p-4 mt-4" data-testid="crate-calc-result-card">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Package className="w-4 h-4 text-blue-600" /> Result
        </div>
        <div className="mt-3 border border-slate-200 rounded-2xl p-6 text-center">
          <div className="text-eyebrow">Crates that fit</div>
          <div className="text-5xl font-bold text-blue-600 tabular-nums my-1.5" data-testid="crate-calc-result-capacity">{best.total}</div>
          <div className="text-xs text-slate-500">{best.along} along length × {best.across} across width × {best.layers} high</div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="border border-slate-200 rounded-2xl p-4">
            <div className="text-eyebrow">Volume used</div>
            <div className="text-xl font-bold text-amber-600 tabular-nums mt-1" data-testid="crate-calc-volume-used">{usedPct.toFixed(1)}%</div>
          </div>
          <div className="border border-slate-200 rounded-2xl p-4">
            <div className="text-eyebrow">Space left over</div>
            <div className="text-xl font-bold text-blue-600 tabular-nums mt-1">{(100 - usedPct).toFixed(1)}%</div>
          </div>
          <div className="border border-slate-200 rounded-2xl p-4">
            <div className="text-eyebrow">Body volume</div>
            <div className="text-xl font-bold text-slate-900 tabular-nums mt-1">{bodyM3.toFixed(2)} m³</div>
          </div>
          <div className="border border-slate-200 rounded-2xl p-4">
            <div className="text-eyebrow">Leftover volume</div>
            <div className="text-xl font-bold text-slate-900 tabular-nums mt-1">{Math.max(0, bodyM3 - usedM3).toFixed(2)} m³</div>
          </div>
        </div>
        <div className="mt-4 h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-blue-600 rounded-full transition-all duration-300" style={{ width: `${Math.min(100, usedPct)}%` }} />
        </div>
        <div className="mt-3 text-xs text-slate-600">→ Best crate orientation: {orientL} × {orientW} × {crate.h} mm</div>
      </div>
    </div>
  );
}
